import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { storiesActions, storiesSelector } from '$store/stories/index';
import { toastActions } from '$store/toast';
import { Api } from '$api';
import { t } from '$translation/helper';
import {
  DeleteStoryAction,
  GetStoriesAction,
  LoadViewsAction,
  ReadOneAction,
  UploadStoryAction,
} from '$store/stories/interface';
import { currentUserSelector, mainActions } from '$store/main';
import { batchActions } from '$store';
import { formatFileSize, getVideoDuration, readFile, statReachGoal } from '$utils';
import {
  shouldUseMultipart,
  MAX_FILE_SIZE_UNVERIFIED,
  MAX_FILE_SIZE_VERIFIED,
  isVideoFile,
  detectVideoContentType,
} from '$utils/multipartUpload';
import { multipartUploadSaga } from '$store/attach/multipartUpload';
import axios from 'axios';
import { StoriesGroup, UserModel } from '$store/models';
import { hideModal } from '$navigation/helper';

function* getStoriesWorker(action: GetStoriesAction) {
  const { userId, storyId } = action.payload;
  yield put(storiesActions.setLoading(true));
  try {
    const {
      stories,
      users,
    }: { stories: StoriesGroup[]; users: { [index: number]: UserModel } } = yield call(
      Api.post,
      '/stories',
      {
        userId,
      },
    );

    if (storyId && !stories.some((u) => u.stories.some((s) => s.id === storyId))) {
      hideModal();
      throw new Error('Story not found');
    }

    yield put(
      batchActions(mainActions.setUsers(users), storiesActions.setStories(stories)),
    );
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  } finally {
    yield put(storiesActions.setLoading(false));
  }
}

function* uploadStoryWorker(action: UploadStoryAction) {
  const { file } = action.payload;
  yield put(toastActions.setToastLoading(t('stories_uploading')));
  try {
    const form = new FormData();
    const dataUrl = yield call(URL.createObjectURL, file);
    const isVideo = isVideoFile(file);
    if (isVideo) {
      const duration = yield call(getVideoDuration, dataUrl);
      if (duration >= 10) {
        throw new Error(t('stories_video_duration_limit'));
      }
    }
    form.append('file', file);
    form.append('album_id', `-7`);
    let mediaId = 0;
    if (!isVideo) {
      const photo = yield call(Api.post, '/photos/upload', form);
      mediaId = photo.photo_id;
    } else {
      const user = (yield select(currentUserSelector)) as UserModel;
      const maxSize =
        user.verified !== 0 ? MAX_FILE_SIZE_VERIFIED : MAX_FILE_SIZE_UNVERIFIED;
      if (file.size > maxSize) {
        throw new Error(t('video_size_error', { size: formatFileSize(maxSize) }));
      }

      if (shouldUseMultipart(file.size)) {
        const { uploadData } = yield call(multipartUploadSaga, file);
        const video = yield call(Api.post, '/video/create', uploadData);
        mediaId = video.videoId;
      } else {
        const contentType = yield call(detectVideoContentType, file);
        const uploadData = yield call(Api.post, '/video/upload_url', {
          contentType,
        });
        yield call(axios.put, uploadData.url, file, {
          headers: uploadData.headers,
        });
        const video = yield call(Api.post, '/video/create', uploadData);
        mediaId = video.videoId;
      }
    }

    const stories = yield call(Api.post, '/stories/publish', {
      mediaType: !isVideo ? 'photo' : 'video',
      mediaId: mediaId,
    });

    yield put(toastActions.hideToast());

    yield put(
      batchActions(
        storiesActions.setStories(stories.stories),
        mainActions.setUsers(stories.users),
      ),
    );

    yield call(statReachGoal, 'upload_story');
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* readOneWorker(action: ReadOneAction) {
  const { duration, storyId } = action.payload;
  try {
    yield call(Api.post, '/stories/read', {
      storyIds: [storyId],
      duration: [duration],
    });
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* deleteStoryWorker(action: DeleteStoryAction) {
  yield put(toastActions.setToastLoading(t('stories_delete_pending')));
  try {
    yield call(Api.post, `/stories/${action.payload}/delete`);
    yield put(storiesActions.deleteStoryFromStore(action.payload));
    yield put(toastActions.hideToast());
    hideModal();
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* loadViewsWorker(action: LoadViewsAction) {
  const { viewsNextFrom } = yield select(storiesSelector);
  yield put(storiesActions.setLoadingViews(true));
  try {
    const { views, users, nextFrom } = yield call(
      Api.post,
      `/stories/${action.payload}/views`,
      {
        startFrom: viewsNextFrom,
      },
    );
    yield put(
      batchActions(
        mainActions.setUsers(users),
        storiesActions.addViews({
          views,
          nextFrom,
        }),
      ),
    );
  } catch (e) {
    yield put(storiesActions.setViewsLoadingFailed(true));
  } finally {
    yield put(storiesActions.setLoadingViews(false));
  }
}

export function* storiesSaga() {
  yield all([takeLatest(storiesActions.deleteStory, deleteStoryWorker)]);
  yield all([takeLatest(storiesActions.getStories, getStoriesWorker)]);
  yield all([takeLatest(storiesActions.uploadStory, uploadStoryWorker)]);
  yield all([takeLatest(storiesActions.readOne, readOneWorker)]);
  yield all([takeLatest(storiesActions.loadViews, loadViewsWorker)]);
}
